import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { levelForSales } from '@/utils/constants';
// @mui
import { Box, Drawer, Stack } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// config
import { NAV } from '../../../config-global';
// components
import Iconify from '../../../components/iconify';
import Logo from '../../../components/logo';
import { NavSectionVertical } from '../../../components/nav-section';
import Scrollbar from '../../../components/scrollbar';
//
import { useSessionContext } from '../../../auth/useSessionContext';
import NavToggleButton from './NavToggleButton';
import Navigation from './config-navigation';
import { BillingCard } from './BillingCard';
import { NavEndSentinel, NavScrollCue } from './NavScrollCue';
import BusinessSwitcher from './BusinessSwitcher';
import { useBusiness } from '../../../contexts/BusinessContext';
import {
  getBusinessLockReason,
  isBusinessLocked,
  withLockedNavItems,
} from '../../../utils/businessLock';

// ----------------------------------------------------------------------

NavVertical.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function NavVertical({ openNav, onCloseNav }) {
  const navConfig = Navigation();

  const { activeBusiness } = useBusiness();

  const isLocked = isBusinessLocked(activeBusiness);
  const lockReason = getBusinessLockReason(activeBusiness);

  // O Iconify esconde o icone do leitor de tela por padrao (aria-hidden), e so o
  // aria-label nao desfaz isso. O cadeado carrega o motivo do bloqueio, entao
  // leva o aria-hidden={false} junto do nome.
  const navData = useMemo(
    () =>
      isLocked
        ? withLockedNavItems(
            navConfig,
            <Iconify
              icon="solar:lock-keyhole-bold"
              width={16}
              role="img"
              aria-label={lockReason}
              aria-hidden={false}
              sx={{ color: 'text.disabled' }}
            />
          )
        : navConfig,
    [isLocked, lockReason, navConfig]
  );

  const { pathname } = useLocation();

  const { user } = useSessionContext();
  // const companies = user?.companies || [];

  // const [company_id, setCompanyId] = useState(companies[0]?.id || '');

  const isDesktop = useResponsive('up', 'lg');

  // Só a gaveta temporária (abaixo de `lg`) tem a pista: é ela que abre no celular.
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const handleEndVisibility = useCallback((visible) => setHasMoreBelow(!visible), []);

  const totalSales = user?.totalSales || 0;

  const currentLevel = levelForSales(totalSales);
  const nextAward = currentLevel.max_value || 0;
  const prizeAchieved = totalSales > nextAward;

  const [previousTotalSales, setPreviousTotalSales] = useState(0);
  const [triggerLoginEffect, setTriggerLoginEffect] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    const lastKnownSales = localStorage.getItem('lastKnownSales');
    const lastKnownLevel = localStorage.getItem('lastKnownLevel');

    if (!hasInitialized && totalSales > 0) {
      if (!lastKnownSales || !lastKnownLevel || lastKnownLevel !== currentLevel.name) {
        setTriggerLoginEffect(true);
        setTimeout(() => setTriggerLoginEffect(false), 1000);
      }

      localStorage.setItem('lastKnownSales', totalSales.toString());
      localStorage.setItem('lastKnownLevel', currentLevel.name);
      setHasInitialized(true);
    }

    setPreviousTotalSales(totalSales);
  }, [totalSales, hasInitialized, currentLevel]);

  useEffect(() => {
    if (!user) {
      localStorage.removeItem('lastKnownSales');
      localStorage.removeItem('lastKnownLevel');
      setHasInitialized(false);
    }
  }, [user]);

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  /* useEffect(() => {
    AuthenticatedHttp.interceptors.request.use((config) => {
      if (company_id) config.headers.Http_collaborator_id = company_id;
      return config;
    })
  }, [company_id]); */

  // const onChangeCompany = (value) => setCompanyId(value);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        spacing={1}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
        }}
      >
        <Logo />

        <BusinessSwitcher />
      </Stack>

      <NavSectionVertical data={navData} onItemClick={onCloseNav} />

      {/* A meta é acompanhamento, não navegação. */}
      {!isLocked && (
        <Box sx={{ px: 2.5, pt: 2, pb: 3, flexShrink: 0 }}>
          <Link to="/dashboard/awards" className="cursor-pointer">
            <BillingCard
              totalSales={totalSales}
              nextAward={nextAward}
              showConfetti={prizeAchieved}
              previousTotalSales={previousTotalSales}
              triggerLoginEffect={triggerLoginEffect}
            />
          </Link>
        </Box>
      )}

      {!isDesktop && <NavEndSentinel onVisibilityChange={handleEndVisibility} />}
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_DASHBOARD },
      }}
    >
      <NavToggleButton />

      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              zIndex: 0,
              width: NAV.W_DASHBOARD,
              bgcolor: 'transparent',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV.W_DASHBOARD,
            },
          }}
        >
          {renderContent}
          {hasMoreBelow && <NavScrollCue />}
        </Drawer>
      )}
    </Box>
  );
}
